import { Popover, PopoverSurface } from "@fluentui/react-components";
import { ContextualMenu, DirectionalHint, Icon, IContextualMenuItem, mergeStyles } from "@fluentui/react";
import { MessageProps, MessageRenderer, useTheme } from '@azure/communication-react';
import React, { useMemo } from 'react';
import { iconWrapperStyle } from 'live-lesson/shared/feature-call-ui-react';

interface ModeratorChatMessageRendererProps {
    messageProps: MessageProps;
    defaultOnRender?: MessageRenderer;
    onDeleteMessage: (messageId: string) => void;
}

export const ModeratorChatMessageRenderer = (props: ModeratorChatMessageRendererProps): JSX.Element => {
    const { messageProps, defaultOnRender, onDeleteMessage } = props;
    const msg = messageProps.message;
    const theme = useTheme();

    // Hooks must run unconditionally on every render (Rules of Hooks); branch only in the
    // returned JSX below. `messageId` is narrowed here so the delete action stays type-safe.
    const messageId = msg.messageType === 'chat' ? msg.messageId : undefined;

    const customContextMenuItems = useMemo<IContextualMenuItem[]>(() => [
        {
            key: 'deleteMessage',
            text: 'Delete',
            iconProps: { iconName: 'Delete' },
            onClick: () => {
                if (messageId) {
                    onDeleteMessage(messageId);
                }
                return true;
            },
        },
    ], [messageId, onDeleteMessage]);

    // internal popover visible state.
    // The popover can still be kept open while the context menu is open
    const [shouldPopoverBeVisible, setShouldPopoverBeVisible] = React.useState(false);

    const messageActionButtonRef = React.useRef<HTMLDivElement>(null);
    const [showContextualMenu, setShowContextualMenu] = React.useState(false);
    const onShowContextualMenu = React.useCallback((ev: React.MouseEvent<HTMLElement>) => {
        ev.preventDefault(); // don't navigate
        setShowContextualMenu(true);
    }, []);
    const onHideContextualMenu = React.useCallback(() => setShowContextualMenu(false), []);

    const renderedMessage = React.useMemo(
        () => (defaultOnRender ? defaultOnRender(messageProps) : <></>),
        [defaultOnRender, messageProps]
    );
    const iconStyle = useMemo(() => iconWrapperStyle(theme, showContextualMenu), [theme, showContextualMenu]);

    // All messages with type !== chat and all own messages are rendered using the default renderer.
    if (msg.messageType !== 'chat' || msg.mine || !defaultOnRender) {
        return defaultOnRender ? defaultOnRender(messageProps) : <></>;
    }

    if (!msg.senderId) {
        throw new Error("'msg.senderId' can not be undefined to render the message");
    }

    // Show context menu on button click and anchor it to the button
    return (
        <>
            <Popover
                openOnHover
                // openOnContext
                mouseLeaveDelay={0}
                positioning={{
                    // Align to the top right of the message bubble
                    // Add negative
                    offset: { mainAxis: -8, crossAxis: 0 },
                    align: 'end',
                    position: 'above',

                }}
                // open on hover _or_ click (mobile)

                // keep the popover open while the context menu is open
                open={shouldPopoverBeVisible || showContextualMenu}
                onOpenChange={(_e, data) => {
                    if (data.open) {
                        setShouldPopoverBeVisible(true);
                    } else {
                        setShouldPopoverBeVisible(false);
                    }
                }}

            >
                <div>
                    {renderedMessage}
                </div>
                <PopoverSurface
                    style={{ padding: 0 }}
                    className={mergeStyles({ 'root': { padding: 0, top: '8px !important', backgroundColor: 'blue' } })}
                >
                    <div
                        key="menuButton"
                        className="chat-message-action-icon"
                        ref={messageActionButtonRef}
                        onClick={onShowContextualMenu}
                    >
                        <Icon iconName="ChatMessageOptions" aria-label={"Weitere Optionen"} styles={iconStyle} />
                    </div>


                    <ContextualMenu
                        items={customContextMenuItems}
                        target={messageActionButtonRef.current}
                        directionalHint={DirectionalHint.topAutoEdge}
                        hidden={!showContextualMenu}
                        onItemClick={onHideContextualMenu}
                        onDismiss={onHideContextualMenu}
                    />
                </PopoverSurface>
            </Popover>
        </>
    );
}
